<script lang="ts">
import { type ComboboxVirtualizerProps } from 'reka-ui';

import type {
  KaVeeFormFieldPropsContract,
  KaVeeFormFieldWithoutInputEmitContract,
} from '@/kasten-components/components/KaVeeFormField';

import { provideComboboxContext } from './context';
import KaComboboxCreateOption from './KaComboboxCreateOption.vue';
import { type KaComboboxGroupProps } from './KaComboboxGroup.vue';
import { type KaComboboxOptionProps } from './KaComboboxOption.vue';

export type KaComboboxVirtualizerProps = Omit<ComboboxVirtualizerProps, 'options'>;

export type KaComboboxCreateOptionConfig = {
  /** When the create row appears. @default 'empty' — only when a search yields zero matches */
  when?: 'empty' | 'always';
  /** @default 'bottom' */
  position?: 'top' | 'bottom';
  /**
   * Keep the row when the typed term exactly equals an existing option's value.
   * @default false — an exact match offers selection, not a duplicate create.
   */
  offerDuplicateCreation?: boolean;
};

export type KaComboboxProps<
  T extends KaComboboxOptionProps | KaComboboxGroupProps | null | undefined =
    KaComboboxOptionProps | KaComboboxGroupProps | null | undefined,
  M extends boolean = false,
> = KaVeeFormFieldPropsContract<MaybeMultiple<T, M> | null> & {
  id?: string;
  modelValue?: MaybeMultiple<T, M> | null;
  dataTestid?: string;
  name: string;
  label: string;
  hideLabel?: boolean;
  description?: string;
  hideDescription?: boolean;
  multiple?: M & boolean;
  hideBadges?: boolean;
  required?: boolean;
  ariaRequired?: boolean;
  left?: IconOptions;
  right?: IconOptions;
  arrow?: IconOptions;
  hideArrow?: boolean;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  /** Used to link an error message element, handled outside of this component, to this component accessibly. This should not be used if the error message is provided via the `errorMessage` prop.
   * This is useful when you want to share the same error message for multiple inputs, such as in a fieldset.
   * The error message element should have an `id` that matches this value.
   * This will also be used to set the `aria-errormessage` attribute on the input element.
   * If not provided, a fallback ID will be generated based on the input's ID.
   * @default undefined
   */
  errorMessageId?: string;
  /** Whether to visually hide the error message so that it is still present in the DOM and only visible to screen-readers. */
  hideErrorMessage?: boolean;
  /** Remove the error message from the DOM completely. This will keep aria invalid properties and error styling, but allow you to put your own error message in the DOM.
   * This is most likely used when you use this input in a fieldset with other inputs that share the same error message and you want to see the error styling but tie the error message to the fieldset instead of the input.
   * @default false
   */
  showErrorStylesOnly?: boolean;
  clearButtonClasses?: VueClass;
  clearable?: boolean;
  clearButtonLabel?: string;
  anchorClasses?: VueClass;
  triggerClasses?: VueClass;
  renderOptionsInPlace?: boolean;
  teleportProps?: ComboboxPortalProps;
  optionsZIndexClass?: VueClass;
  optionsContainerClasses?: VueClass;
  inputClasses?: VueClass;
  comboboxContentProps?: ComboboxContentProps;
  options?: (KaComboboxOptionProps | KaComboboxGroupProps)[];
  inputDisplayValue?: (item?: KaComboboxOptionProps) => string;
  viewportClasses?: VueClass;
  filterOptions?: (
    options: (KaComboboxOptionProps | KaComboboxGroupProps)[],
    searchTerm: string,
  ) => MaybePromise<(KaComboboxOptionProps | KaComboboxGroupProps)[]>;
  /** Debounce delay in milliseconds for filtering when using async filterOptions */
  filterDebounceMs?: number;
  /** Whether to show a loading indicator when filtering */
  showFilterLoading?: boolean;
  /** Custom loading text */
  filterLoadingText?: string;
  /**
   * Enables the create row when set. Always an object; every key is optional and merged
   * over defaults, so `{}` means "show at the bottom when a search has no matches".
   * @defaultValue undefined (no create row)
   */
  createOption?: KaComboboxCreateOptionConfig;
  /**
   * Handler for the create flow. Receives the typed value and decides whether
   * the input is accepted. Return `false` to keep the typed text in the
   * combobox (e.g. on a validation failure so the user can fix the typo);
   * any other return value (including `undefined`) clears the search term in
   * `multiple` mode.
   *
   * Only the multi-select branch auto-clears — single-select preserves the
   * typed text after create regardless of the return value.
   */
  onCreate?: (option: string) => boolean | void;
  /** Label template for the create option. Use `$searchTerm` as the typed-value placeholder. */
  createLabel?: string;
  /**
   * Label for the create option when there is no search term (e.g. an always-present create
   * row before the user types). Never falls back to `createLabel`, so its template cannot
   * degrade to `Create ""`.
   * @default 'Create new…'
   */
  createLabelEmpty?: string;
  autoFocus?: boolean;
  autoFocusDelayMs?: number;
  openOnFocus?: boolean;
  virtualized?: boolean | KaComboboxVirtualizerProps;
  paginated?: Omit<KaInfiniteLoaderProps, 'hideBottomMessage' | 'data'> & {
    onLoadMore: () => void;
  };
  /** Only use this property if you plan to add the asterisk in the slot content of the label.
   * A required field MUST have a visual indicator to be accessible.
   *
   * https://www.w3.org/WAI/WCAG22/Techniques/aria/ARIA2
   *
   * https://veeam.hub.essentia11y.com/manual-evaluations/67af37db16faa72414aa1705/results/AMP256/view?skipScanStandards=true&evaluationStandard=WCAG_v22_AAA&status=toReview,open,partiallyFixed,notFixed,cannotBeFixed,validationRequested&issueId=AMP256&linkedPropertyData=6762fe26f5d3a5eaed29f2c8%7C6723d15450ee91f82f056d41
   */
  hideRequiredAsterisk?: boolean;
};

export type KaCombobox2Emits<
  T extends KaComboboxOptionProps | KaComboboxGroupProps | null | undefined =
    KaComboboxOptionProps | KaComboboxGroupProps | null | undefined,
  M extends boolean = false,
> = KaVeeFormFieldWithoutInputEmitContract<MaybeMultiple<T, M> | null> & {
  (e: 'update:modelValue', value?: MaybeMultiple<T, M> | null): void;
  (e: 'change', value?: MaybeMultiple<T, M> | null): void;
  (e: 'blur', event: FocusEvent): void;
  (e: 'open'): void;
  (e: 'close'): void;
};
</script>
<script
  setup
  lang="ts"
  generic="
    T extends KaComboboxOptionProps | KaComboboxGroupProps | null | undefined,
    M extends boolean = false
  "
>
import { computedAsync, refDebounced } from '@vueuse/core';
import { isEqual } from 'ohash/utils';
import {
  ComboboxAnchor,
  ComboboxContent,
  type ComboboxContentProps,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxPortal,
  type ComboboxPortalProps,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxVirtualizer,
  Label,
  TagsInputInput,
  TagsInputRoot,
  useFilter,
} from 'reka-ui';
import type { MaybePromise } from 'vee-validate';
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  readonly,
  type Ref,
  ref,
  shallowRef,
  toRef,
  watch,
} from 'vue';

// Import each sub-component from its own entrypoint rather than the `@/kasten-components/components`
// barrel: the barrel pulls in the entire library (incl. chart components that depend on core-js's
// CJS Map polyfill), which fails to load in the Histoire sandbox ("require is not defined").
import { KaDescription } from '@/kasten-components/components/KaDescription';
import {
  KaInfiniteLoader,
  type KaInfiniteLoaderProps,
} from '@/kasten-components/components/KaInfiniteLoader';
import { KaLabel } from '@/kasten-components/components/KaLabel';
import { KaMessage } from '@/kasten-components/components/KaMessage';
import { useDataTestid } from '@/kasten-components/composables';
import type { IconOptions, MaybeMultiple, VueClass } from '@/kasten-components/types';
import { dom, unwrapElement } from '@/kasten-components/utils';
import { cnMerge } from '@/utils/utils.js';
import ChevronUpDownIcon from '~icons/lucide/chevrons-up-down';
import IconClose from '~icons/lucide/x';

import {
  comboboxAnchorVariants,
  comboboxClearButtonVariants,
  comboboxContentVariants,
  comboboxInputVariants,
  comboboxViewportVariants,
} from './combobox-variants';
import KaComboboxGroup from './KaComboboxGroup.vue';
import KaComboboxIconSlot from './KaComboboxIconSlot.vue';
import KaComboboxOption from './KaComboboxOption.vue';
import KaComboboxTag from './KaComboboxTag.vue';

const props = withDefaults(defineProps<KaComboboxProps<T, M>>(), {
  modelValue: undefined,
  description: undefined,
  // @ts-expect-error
  multiple: false,
  hideLabel: false,
  hideDescription: false,
  required: false,
  disabled: false,
  clearable: false,
  renderOptionsInPlace: false,
  inputDisplayValue: (item?: KaComboboxOptionProps) => item?.label ?? item?.textValue ?? '',
  showFilterLoading: true,
  filterLoadingText: 'Searching...',
  createOption: undefined,
  resetSearchTermOnSelect: true,
  autoFocus: false,
  autoFocusDelayMs: 0,
  openOnFocus: false,
  hideBadges: false,
});

const slots = defineSlots<{
  label?(props: { label: string }): any;
  description?(props: { description?: string }): any;
  left?(props: { selected?: MaybeMultiple<T, M> | null }): any;
  right?(props: { selected?: MaybeMultiple<T, M> | null }): any;
  arrow?(props: Record<string, never>): any;
  optionItem?(props: { option: KaComboboxOptionProps }): any;
  filterLoading?(props: { isFiltering: boolean }): any;
  filterError?(props: { error: string }): any;
}>();

const emit = defineEmits<KaCombobox2Emits<T, M>>();

// LEAVE THIS HERE TO PREVENT KAVEEFORMFIELD INPUT EMITS FROM BEING INHERITED, otherwise validation WILL BE BROKEN
defineOptions({
  inheritAttrs: false,
});

const name = computed(() => props.name);
const id = computed(() => props.id ?? name.value);
const { dataTestid, suffix } = useDataTestid(computed(() => props.dataTestid ?? id.value));
const labelId = computed(() => `${id.value}-label`);
const inputId = computed(() => `${id.value}-input`);

const descriptionId = computed(() =>
  shouldDisplayDescription.value ? `${id.value}-description` : undefined,
);
const shouldDisplayDescriptionMargin = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);

const isRequired = computed(() => props.required || props.ariaRequired);

const shouldDisplayDescription = computed(
  // Required/invalid state is conveyed by the input's aria-required/aria-invalid, so no extra
  // sr-only description is needed for those (it would be announced twice). Multiple-select always
  // renders a description so the field can announce the current selection to assistive tech (the
  // visual badges are not exposed as the field's value) — matching KaSelect.
  () => props.description || slots.description || props.multiple,
);

const fallbackErrorMessageId = computed(() => `${id.value}-error-message`);
const hasError = computed(() => props.errorMessage !== undefined);
const computedErrorMessageId = computed(() => {
  if (props.showErrorStylesOnly) return props.errorMessageId || undefined;
  return hasError.value ? fallbackErrorMessageId.value : undefined;
});
const isInvalid = computed(() => props.showErrorStylesOnly || hasError.value);

const searchTerm = ref('');

const isOpen = ref(false);
const comboboxAnchor = ref<HTMLElement | null>(null);
const triggerButton = ref<HTMLElement | null>(null);
const clearButton = ref<HTMLButtonElement | null>(null);
const comboboxInput = ref<HTMLInputElement | null>(null);
const comboboxContent = ref<HTMLElement | null>(null);
const internalValue = ref(props.modelValue) as Ref<MaybeMultiple<T, M> | null | undefined>;
const isFiltering = shallowRef(false);

const filterError = ref<string | null>(null);
const isUserTyping = ref(false); // Track if user is actively typing vs. just opening combobox

const options = computed(() => props.options);

const virtualized = computed<KaComboboxVirtualizerProps | undefined>(() => {
  if (typeof props.virtualized === 'boolean') {
    return props.virtualized
      ? {
          estimateSize: 36,
          overscan: 10,
          textContent: (option) => {
            const opt = option as KaComboboxOptionProps;
            return opt.textValue ?? opt.label ?? '';
          },
        }
      : undefined;
  }

  return props.virtualized;
});

const virtualizedBindings = computed<KaComboboxVirtualizerProps>(() => {
  return (virtualized.value ?? {}) as KaComboboxVirtualizerProps;
});

const handleUpdateModelValue = async (value?: MaybeMultiple<T, M> | null) => {
  internalValue.value = value;
  emit('update:modelValue', value);
  emit('change', value);

  searchTerm.value = '';

  // reka-ui >= 2.9 no longer keeps focus on the combobox input after the
  // selection changes (was implicit in 2.5). Restore it so Tab/Esc/typing
  // continue from the input as users expect from the WAI-ARIA combobox pattern.
  // This also covers tag removal in multiple mode: clicking a badge's X button
  // unmounts that button, which would otherwise drop focus to <body>.
  await focus();
};

const onCreateOption = (value: string) => {
  isOpen.value = false;
  const accepted = props.onCreate?.(value);
  if (props.multiple && accepted !== false) {
    searchTerm.value = '';
  }
};

const clearSelectedOption = async () => {
  if (!props.disabled) {
    internalValue.value = undefined;
    searchTerm.value = '';

    // Emit updates
    emit('update:modelValue', internalValue.value);
    emit('change', internalValue.value);

    isUserTyping.value = false;

    // Focus back on the trigger button without scrolling
    await focus();
  }
};

const openCombobox = async () => {
  if (props.disabled) return;

  if (!isOpen.value) {
    isOpen.value = true;
    await focus();
  }
};

const focus = async () => {
  dom(comboboxInput)?.focus({ preventScroll: true });
  await nextTick();
};

const handleOpen = async (open: boolean) => {
  if (props.disabled) return;
  isOpen.value = open;
  await nextTick();
};

const shouldDispatchBlur = (event: FocusEvent) => {
  const nextTarget = event.relatedTarget ?? document.activeElement;

  // Blur didn't result in focus on another focusable element
  if (nextTarget === null) return true;

  // If target is not an HTML Node - we can't react on it anyhow. Consider this a blur.
  if (!(nextTarget instanceof HTMLElement)) return true;

  // Check if the next target is a badge remove button within our combobox. This is NOT redundant
  // with the `anchorElement.contains(nextTarget)` check below: clicking a tag's remove button
  // detaches it from the DOM as the tag is removed, so by the time this blur fires the button is no
  // longer a descendant of the anchor and `contains` returns false — which would wrongly dispatch
  // blur and close the listbox. The detached element still carries its `aria-label`/test id, so we
  // match on those instead. (Verified: removing this branch breaks multiple.spec.ts "removes the
  // selected options".)
  const isTagRemoveButton =
    nextTarget?.getAttribute('aria-label')?.startsWith('Remove ') ||
    nextTarget?.closest('[data-testid$="-remove-button"]') !== null;

  // If we blurred and focused on a badge remove button, don't dispatch blur as it's part of our component
  if (isTagRemoveButton && props.multiple) {
    return false;
  }

  const anchorElement = unwrapElement(comboboxAnchor);
  const triggerElement = unwrapElement(triggerButton);
  const clearButtonElement = unwrapElement(clearButton);
  const comboboxInputElement = unwrapElement(comboboxInput);
  const optionsContainer = unwrapElement(comboboxContent);

  const anchorIsNotFocused = !anchorElement || !anchorElement.contains(nextTarget);
  const triggerIsNotFocused = !triggerElement || !triggerElement.contains(nextTarget);
  const clearButtonIsNotFocused = !clearButtonElement || !clearButtonElement.contains(nextTarget);
  const comboboxInputIsNotFocused =
    !comboboxInputElement || !comboboxInputElement.contains(nextTarget);
  const optionsContainerIsNotFocused = !optionsContainer || !optionsContainer.contains(nextTarget);

  return (
    anchorIsNotFocused &&
    triggerIsNotFocused &&
    clearButtonIsNotFocused &&
    comboboxInputIsNotFocused &&
    optionsContainerIsNotFocused
  );
};

const handleBlur = (event: FocusEvent) => {
  setTimeout(() => {
    if (shouldDispatchBlur(event)) {
      emit('blur', event);

      if (isOpen.value) {
        isOpen.value = false;
      }

      // Drop in-progress search text on blur, falling back to the selected value's display, so a picker
      // never strands a typed string that misrepresents the committed value (K10-37124).
      const selected = internalValue.value;
      const display =
        !props.multiple && selected != null
          ? props.inputDisplayValue(selected as KaComboboxOptionProps)
          : '';
      if (searchTerm.value !== display) searchTerm.value = display;
      isUserTyping.value = false;
    }
  });
};

const getSingleOptionKey = (option: KaComboboxOptionProps) => {
  // Serialize the value rather than `.toString()` it — object values would otherwise all collapse to
  // "[object Object]" and collide. `value` holds plain data (icons live on left/right), so it is
  // JSON-serializable; fall back to label/textValue for the synthetic "clear" option (no value).
  return option.value != null
    ? JSON.stringify(option.value)
    : (option.label ?? option.textValue ?? 'clear');
};

const isGroup = (
  option: KaComboboxOptionProps | KaComboboxGroupProps,
): option is KaComboboxGroupProps => {
  return !('value' in option);
};

// Stable v-for key for the option list (groups + options). Key on data identity — never
// `JSON.stringify(option)`, whose `left`/`right` IconOptions can hold Vue VNodes that throw on
// circular serialization.
const optionKey = (option: KaComboboxOptionProps | KaComboboxGroupProps): string =>
  isGroup(option) ? `group-${option.label}` : `option-${JSON.stringify(option.value)}`;

// Computed property for safely getting the array for TagsInputRoot
const multipleValues = computed(
  () => internalValue.value as KaComboboxOptionProps[] | null | undefined,
);

const hasSelectedOptions = computed(() =>
  Array.isArray(internalValue.value) ? internalValue.value.length > 0 : internalValue.value != null,
);

// Labels of the currently-selected options, used for the sr-only selection announcement in multiple
// mode (the visual selection isn't exposed as the field's accessible value).
const selectedLabels = computed(() =>
  (multipleValues.value ?? []).map((option) => option.label).filter(Boolean),
);

// Count for the announcement — derived from the full selection, not `selectedLabels` (which drops
// options without a `label`), so it always matches the number of selections.
const selectedCount = computed(() => multipleValues.value?.length ?? 0);

// Update function for TagsInputRoot
const updateMultipleValues = async (value: any) => {
  const isEmptyArrayWithEmptyString = Array.isArray(value) && value.length === 1 && value[0] === '';
  const normalizedValue =
    isEmptyArrayWithEmptyString || value === '' ? undefined : (value as MaybeMultiple<T, M>);

  handleUpdateModelValue(normalizedValue);
};

const { contains } = useFilter({ sensitivity: 'base' });

const defaultFilterFn = (
  options: (KaComboboxOptionProps | KaComboboxGroupProps)[],
  searchTerm: string,
): (KaComboboxOptionProps | KaComboboxGroupProps)[] | [] => {
  if (searchTerm === '') return options;

  const filteredOptionsLocal: (KaComboboxOptionProps | KaComboboxGroupProps)[] = [];

  options.forEach((option) => {
    if (isGroup(option)) {
      // If it's a group, filter its options
      if (option.options) {
        const filteredGroupOptions = option.options.filter((groupOption) =>
          contains(groupOption.textValue ?? groupOption.label ?? '', searchTerm),
        );
        if (filteredGroupOptions.length > 0) {
          filteredOptionsLocal.push({
            ...option,
            options: filteredGroupOptions,
          });
        }
      }
    } else if (contains(option.textValue ?? option.label ?? '', searchTerm)) {
      // If it matches, add it to the filtered options
      filteredOptionsLocal.push(option);
    }
  });

  return filteredOptionsLocal;
};

const filterFn = computed(() => {
  return props.filterOptions ?? defaultFilterFn;
});

// Check if the filter function is potentially async by testing with empty inputs
const isFilterFnAsync = computed(() => {
  if (!props.filterOptions) return false; // default function is sync

  // Test the function with empty arrays to see if it returns a Promise
  const testResult = props.filterOptions([], '');
  return (
    testResult instanceof Promise || (testResult && typeof (testResult as any).then === 'function')
  );
});

// Compute the effective debounce value based on user preference and function type
const effectiveDebounceMs = computed(() => {
  // If user explicitly provided a debounce value, use it
  if (props.filterDebounceMs !== undefined) {
    return props.filterDebounceMs;
  }

  // If no debounce provided, use 300ms for async functions, 0ms for sync
  return isFilterFnAsync.value ? 300 : 0;
});

const searchTermDebounced = refDebounced(searchTerm, effectiveDebounceMs);
const filteredOptions = computedAsync<(KaComboboxOptionProps | KaComboboxGroupProps)[] | undefined>(
  async () => {
    // console.log('Filtering options with search term:', searchTermDebounced.value);
    try {
      if (!options.value) return;

      const effectiveSearchTerm =
        !props.filterOptions && (searchTermDebounced.value === '' || !isUserTyping.value)
          ? ''
          : searchTermDebounced.value;

      const result = await filterFn.value(options.value, effectiveSearchTerm);
      return result;
    } catch (error) {
      filterError.value =
        error instanceof Error ? error.message : 'An error occurred while filtering';
      throw error;
    }
  },
  props.options,
  { lazy: true, evaluating: isFiltering },
);

// flatten filtered options into an array of KaComboboxOptionProps for easier searching
const flattenedFilteredOptions = computed(() => {
  const result: KaComboboxOptionProps[] = [];

  if (!filteredOptions.value) return result;

  filteredOptions.value.forEach((option) => {
    if (isGroup(option)) {
      if (option.options) {
        result.push(...option.options);
      }
    } else {
      result.push(option);
    }
  });

  return result;
});

const shouldShowContent = computed(() => {
  if (searchTerm.value) return true;
  if (isFiltering.value) return true;
  if (filteredOptions.value && filteredOptions.value.length > 0) return true;
  // Keep the panel open for an always-present create row even with no options and no search.
  if (showCreateRow.value) return true;
  return false;
});

// One source for the empty-state line: <ComboboxEmpty> renders it for plain comboboxes, the
// static row renders it when an always-create ComboboxItem keeps ComboboxEmpty suppressed.
const EMPTY_STATE_MESSAGE = 'Sorry, no options found.';
const EMPTY_STATE_CLASSES = 'select-none px-4 py-2';

const shouldShowEmptyState = computed(() => {
  return (
    !isFiltering.value &&
    ((!!searchTerm.value && filteredOptions.value && filteredOptions.value.length === 0) ||
      !filteredOptions.value) &&
    !props.createOption
  );
});

const compare = (a?: KaComboboxOptionProps, b?: KaComboboxOptionProps) => {
  if (!a || !b) {
    return false;
  }

  if (a.value === undefined || b.value === undefined) {
    return false;
  }

  if (typeof a.value === 'string') {
    return a.value === b.value;
  }

  return isEqual(a.value, b.value);
};

// "always" timing shows the create row even before the user types and alongside existing options,
// so it can sit as the last entry in the list; the default only appears when a search has 0 matches.
// Every key of the prop is optional; merge over defaults once so downstream logic reads a
// single fully-populated config instead of re-interpreting the prop shape.
const createOptionConfig = computed<Required<KaComboboxCreateOptionConfig> | undefined>(() =>
  props.createOption
    ? { when: 'empty', position: 'bottom', offerDuplicateCreation: false, ...props.createOption }
    : undefined,
);

const isAlwaysCreate = computed(() => createOptionConfig.value?.when === 'always');

const showCreateRow = computed(() => {
  const config = createOptionConfig.value;
  if (!config) return false;
  // Create-on-empty timing still requires an active search term; "always" does not.
  if (!isAlwaysCreate.value && !searchTerm.value) return false;

  if (isAlwaysCreate.value && !config.offerDuplicateCreation) {
    const newItem = {
      label: searchTerm.value,
      value: searchTerm.value,
      textValue: searchTerm.value,
    };
    // An exact match means the typed thing already exists, so offer selection, not a duplicate
    // create. Labels carry the match for object-valued options, which never equal a typed string.
    return !flattenedFilteredOptions.value.find(
      (item) => item.label === searchTerm.value || compare(item, newItem),
    );
  }

  if (isAlwaysCreate.value) return true;

  return !flattenedFilteredOptions.value.length;
});

// With "always" timing the create row is a ComboboxItem, so reka's <ComboboxEmpty> (which under
// ignore-filter renders only when zero items are registered) would hide the empty message. Render
// a plain "no options" line above the create row instead when a search filters everything out.
const shouldShowStaticEmpty = computed(
  () =>
    isAlwaysCreate.value &&
    !isFiltering.value &&
    !!searchTerm.value &&
    flattenedFilteredOptions.value.length === 0,
);

const filterCountAnnouncement = computed(() => {
  if (!isOpen.value || !isUserTyping.value) return '';
  // Suppress while the filter is still settling so we don't announce a stale
  // count between the keystroke and the debounced/async filter result.
  if (isFiltering.value || searchTerm.value !== searchTermDebounced.value) return '';

  const count = flattenedFilteredOptions.value.length + (showCreateRow.value ? 1 : 0);
  if (count === 0) return 'No options available.';
  if (count === 1) return '1 option available.';
  return `${count} options available.`;
});

// Watch for combobox open/close state
watch(
  () => isOpen.value,
  (newIsOpen) => {
    if (newIsOpen) {
      emit('touch');
      emit('open');
      isUserTyping.value = false;
    } else {
      emit('close');
    }
  },
);

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue;
    if (!newValue) {
      searchTerm.value = '';
    } else if (!props.multiple) {
      searchTerm.value = props.inputDisplayValue(newValue as KaComboboxOptionProps);
    }
  },
);

// Provide the computed refs directly (not `toRef(<current value>)`, which snapshots a static string)
// so child options/groups see live updates to the combobox's dataTestid/id.
provideComboboxContext({
  dataTestid: readonly(dataTestid),
  id: readonly(id),
});

onBeforeMount(() => {
  if (
    !!props.virtualized &&
    options.value &&
    options.value.some((option) => isGroup(option)) &&
    process.env.NODE_ENV !== 'production'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[KaCombobox]: Virtualization with grouped options is not supported. The options provided have been flattened to maintain virtualization. Please consider using non-grouped options or disabling virtualization. See https://github.com/unovue/reka-ui/issues/1885 for more details.',
    );
  }
});

// Track the autofocus timer so it can be cleared if the component unmounts before it fires.
let autoFocusTimeoutId: ReturnType<typeof setTimeout> | undefined;

onMounted(async () => {
  if (props.autoFocus) {
    autoFocusTimeoutId = setTimeout(async () => {
      focus();
    }, props.autoFocusDelayMs);
  }

  // reka-ui ComboboxRoot generates hidden inputs for form submission when :name is set.
  // They lack a label, causing an axe-core "label" violation. For scalar values the input is
  // named exactly `name`; for multiple/object values reka serializes each property to a nested
  // name like `name[0][value]` / `name[0][label]`. nextTick ensures reka's post-render watcher
  // has created the inputs before we query them.
  if (props.name) {
    await nextTick();
    document
      .querySelectorAll<HTMLInputElement>(
        `input[data-hidden][name="${props.name}"], input[data-hidden][name^="${props.name}["]`,
      )
      .forEach((hiddenInput) => {
        hiddenInput.setAttribute('aria-label', props.label);
      });
  }
});

onBeforeUnmount(() => {
  if (autoFocusTimeoutId !== undefined) {
    clearTimeout(autoFocusTimeoutId);
  }
});

// reka-ui writes the trigger/input's `aria-controls` once, non-reactively, from
// content's id — stale or empty once open state changes. Sync it live instead.
// K10-36295, remove once fixed upstream: https://github.com/unovue/reka-ui/issues/2597
watch(
  [isOpen, comboboxContent],
  async () => {
    await nextTick();
    const listbox = unwrapElement(comboboxContent);
    for (const elRef of [triggerButton, comboboxInput]) {
      const el = unwrapElement(elRef);
      if (!el) continue;
      if (isOpen.value && listbox?.id) {
        if (el.getAttribute('aria-controls') !== listbox.id) {
          el.setAttribute('aria-controls', listbox.id);
        }
      } else {
        el.removeAttribute('aria-controls');
        // reka's ComboboxInput (via ListboxFilter) keeps aria-activedescendant pointing at
        // the last highlighted option after the listbox closes and unmounts it — a stale id
        // reference axe flags as critical. reka re-binds it when an option is highlighted again.
        el.removeAttribute('aria-activedescendant');
      }
    }
  },
  { flush: 'post', immediate: true },
);

// The removal above is not enough on its own: ListboxFilter re-applies the stale id from its
// cached ref on every input re-render while closed, so strip it again whenever it reappears.
const activedescendantObserver = new MutationObserver(() => {
  const input = dom(comboboxInput);
  if (!isOpen.value && input?.hasAttribute('aria-activedescendant')) {
    input.removeAttribute('aria-activedescendant');
  }
});

watch(
  () => dom(comboboxInput),
  (input) => {
    activedescendantObserver.disconnect();
    if (input) {
      activedescendantObserver.observe(input, { attributeFilter: ['aria-activedescendant'] });
    }
  },
  { flush: 'post', immediate: true },
);

onBeforeUnmount(() => activedescendantObserver.disconnect());

defineExpose({
  inputRef: toRef(() => dom(comboboxInput)),
  focus,
});
</script>
<template>
  <ComboboxRoot
    :model-value="internalValue"
    :open="isOpen"
    :name="name"
    :disabled="props.disabled"
    :by="(a, b) => compare(a as KaComboboxOptionProps, b as KaComboboxOptionProps)"
    ignore-filter
    :multiple="props.multiple"
    reset-search-term-on-select
    open-on-click
    :open-on-focus="props.openOnFocus"
    @update:model-value="
      (value: any) => handleUpdateModelValue(value as MaybeMultiple<T, M> | null)
    "
    @update:open="handleOpen"
  >
    <Label as-child :for="inputId">
      <KaLabel
        :id="labelId"
        :data-testid="suffix('label')"
        :hide-label="props.hideLabel"
        :class="!shouldDisplayDescriptionMargin ? 'mx-0 mb-3 mt-0' : 'm-0'"
        :required="isRequired"
        :hide-required-asterisk="props.hideRequiredAsterisk"
      >
        <slot name="label" v-bind="{ label: props.label }">{{ props.label }}</slot>
      </KaLabel>
    </Label>

    <KaDescription
      v-if="shouldDisplayDescription"
      :id="descriptionId"
      :data-testid="suffix('description')"
      :hide-description="props.hideDescription"
      :class="shouldDisplayDescriptionMargin ? 'mx-0 mb-3 mt-0' : 'm-0'"
    >
      <slot name="description" v-bind="{ description: props.description }">
        <span>{{ props.description }}</span>
      </slot>

      <!--
        Announce the current selection to assistive tech. The visual selection (badges) is not
        exposed as the field's accessible value, so without this a screen reader on the input would
        not say what is selected. Rendered for all multiple-select, not just hideBadges.
      -->
      <template v-if="props.multiple">
        <span v-if="selectedCount" class="sr-only"
          >{{ selectedCount }} options selected{{
            selectedLabels.length ? `: ${selectedLabels.join(', ')}` : ''
          }}.</span
        >
        <span v-else class="sr-only">There are no options selected.</span>
      </template>
    </KaDescription>

    <ComboboxAnchor
      :id="id"
      ref="comboboxAnchor"
      :data-testid="dataTestid"
      :class="
        cnMerge(
          comboboxAnchorVariants({ invalid: isInvalid, disabled: props.disabled, open: isOpen }),
          props.anchorClasses,
        )
      "
      @click.stop="openCombobox"
    >
      <div class="flex w-[calc(100%-52px)] flex-1 justify-between">
        <KaComboboxIconSlot
          v-if="'left' in slots || props.left"
          :data-testid="suffix('left')"
          :icon-options="props.left"
          class="mr-2 size-6 flex-shrink-0 flex-grow-0 text-current"
        >
          <slot name="left" :selected="internalValue" />
        </KaComboboxIconSlot>

        <TagsInputRoot
          v-if="props.multiple"
          :data-testid="suffix('selected')"
          :model-value="multipleValues"
          delimiter=""
          class="flex w-full flex-wrap gap-1"
          @update:model-value="updateMultipleValues"
        >
          <template v-if="!props.hideBadges">
            <KaComboboxTag
              v-for="option in multipleValues"
              :key="getSingleOptionKey(option)"
              :data-testid="suffix(`selected-${option.label}`)"
              :value="option"
              :label="option.label || ''"
              @remove:blur="handleBlur"
            />
          </template>

          <ComboboxInput v-model="searchTerm" as-child>
            <TagsInputInput
              :id="inputId"
              ref="comboboxInput"
              :data-testid="suffix('input')"
              :aria-required="isRequired"
              :aria-describedby="descriptionId"
              :placeholder="props.placeholder"
              :aria-invalid="props.showErrorStylesOnly || hasError"
              :aria-errormessage="computedErrorMessageId"
              :display-value="props.inputDisplayValue"
              :class="
                cnMerge(
                  comboboxInputVariants({
                    layout: 'multiple',
                    invalid: isInvalid,
                    hasSelection: hasSelectedOptions,
                    disabled: props.disabled,
                  }),
                  props.inputClasses,
                )
              "
              @click.stop
              @input="isUserTyping = true"
              @keydown="isUserTyping = true"
              @keydown.enter.prevent
              @blur="handleBlur"
            />
          </ComboboxInput>
        </TagsInputRoot>

        <ComboboxInput
          v-else
          :id="inputId"
          ref="comboboxInput"
          v-model="searchTerm"
          :data-testid="suffix('input')"
          :aria-required="isRequired"
          :aria-describedby="descriptionId"
          :placeholder="props.placeholder"
          :aria-invalid="props.showErrorStylesOnly || hasError"
          :aria-errormessage="computedErrorMessageId"
          :display-value="props.inputDisplayValue"
          :class="
            cnMerge(
              comboboxInputVariants({
                layout: 'single',
                invalid: isInvalid,
                hasSelection: hasSelectedOptions,
                disabled: props.disabled,
              }),
              props.inputClasses,
            )
          "
          @click.stop
          @input="isUserTyping = true"
          @blur="handleBlur"
        />

        <KaComboboxIconSlot
          v-if="'right' in slots || props.right"
          :data-testid="suffix('right')"
          :icon-options="props.right"
          class="ml-2 inline-flex flex-shrink-0 flex-grow-0 text-current"
        >
          <slot name="right" :selected="internalValue" />
        </KaComboboxIconSlot>
      </div>

      <div class="flex flex-shrink-0 flex-grow-0 gap-x-2">
        <button
          v-if="
            props.clearable &&
            !props.disabled &&
            (props.multiple ? multipleValues && multipleValues.length > 0 : !!internalValue)
          "
          ref="clearButton"
          :data-testid="suffix('clear-button')"
          type="button"
          :class="
            cnMerge(
              comboboxClearButtonVariants(),
              { 'cursor-not-allowed': props.disabled },
              props.clearButtonClasses,
            )
          "
          title="Clear selected"
          :aria-label="props.clearButtonLabel || `Clear selected ${props.label}`"
          @click.prevent.stop="clearSelectedOption"
          @blur="handleBlur"
        >
          <IconClose class="size-4" aria-hidden="true" />
        </button>

        <ComboboxTrigger
          ref="triggerButton"
          :aria-labelledby="labelId"
          :aria-describedby="descriptionId"
          :data-testid="suffix('trigger-button')"
          :class="[
            'm-0 flex size-4 flex-shrink-0 flex-grow-0 items-center justify-center p-0',
            { 'cursor-not-allowed': props.disabled },
          ]"
          @click.stop
        >
          <KaComboboxIconSlot
            v-if="!props.hideArrow"
            :data-testid="suffix('arrow')"
            :icon-options="props.arrow"
            class="h-full w-full text-gray-600 outline-none dark:text-gray-500"
          >
            <slot v-if="!props.arrow" name="arrow">
              <ChevronUpDownIcon class="h-full w-full" />
            </slot>
          </KaComboboxIconSlot>
        </ComboboxTrigger>
      </div>
    </ComboboxAnchor>

    <ComboboxPortal
      :disabled="props.renderOptionsInPlace || props.teleportProps?.disabled"
      :to="props.teleportProps?.to ?? 'body'"
      :force-mount="props.teleportProps?.forceMount"
      :defer="props.teleportProps?.defer"
    >
      <ComboboxContent
        v-if="shouldShowContent"
        ref="comboboxContent"
        :position="
          props.renderOptionsInPlace ? 'inline' : (props.comboboxContentProps?.position ?? 'popper')
        "
        :class="
          cnMerge(
            comboboxContentVariants(),
            props.optionsZIndexClass || 'z-20',
            props.optionsContainerClasses,
          )
        "
        :align="props.comboboxContentProps?.align ?? 'start'"
        :avoid-collisions="props.comboboxContentProps?.avoidCollisions ?? true"
        :collision-boundary="props.comboboxContentProps?.collisionBoundary ?? null"
        :collision-padding="props.comboboxContentProps?.collisionPadding ?? 10"
        @focus-outside.prevent
      >
        <div
          role="presentation"
          :data-testid="suffix('viewport')"
          :style="{ 'scrollbar-width': 'auto' }"
          :class="cnMerge(comboboxViewportVariants(), props.viewportClasses)"
        >
          <!-- Loading state -->
          <template v-if="isFiltering && props.showFilterLoading">
            <slot name="filterLoading" v-bind="{ isFiltering }">
              <div class="select-none px-4 py-2 text-gray-500 dark:text-gray-400">
                {{ props.filterLoadingText }}
              </div>
            </slot>
          </template>

          <!-- Error state -->
          <template v-else-if="filterError">
            <slot name="filterError" v-bind="{ error: filterError }">
              <div class="select-none px-4 py-2 text-red-600 dark:text-red-400">
                Error: {{ filterError }}
              </div>
            </slot>
          </template>

          <ComboboxEmpty
            v-else-if="shouldShowEmptyState"
            :data-testid="suffix('empty')"
            :class="EMPTY_STATE_CLASSES"
            >{{ EMPTY_STATE_MESSAGE }}</ComboboxEmpty
          >

          <template v-else>
            <!-- Static empty line for the always-create row (reka's ComboboxEmpty is hidden once
                 the create ComboboxItem registers under ignore-filter). -->
            <div
              v-if="shouldShowStaticEmpty"
              :data-testid="suffix('empty')"
              :class="EMPTY_STATE_CLASSES"
            >
              {{ EMPTY_STATE_MESSAGE }}
            </div>

            <KaComboboxCreateOption
              v-if="showCreateRow && createOptionConfig?.position === 'top'"
              :search-term="searchTerm"
              :create-label="props.createLabel"
              :create-label-empty="props.createLabelEmpty"
              @create="onCreateOption($event)"
            />

            <!-- DO NOT leave commented code in the ComboboxVirtualizer block, it will not render any options at all! -->
            <!-- Note: reka's ComboboxVirtualizer types its slot `option` as the loose `AcceptableValue`
              (string | number | … | null), so the casts to KaComboboxOptionProps below are required —
              unlike the non-virtualized branch, there is no `isGroup` predicate to narrow it. (This note
              is kept OUTSIDE the virtualizer slot per the warning above.) -->
            <template v-if="!!virtualized">
              <ComboboxVirtualizer
                v-slot="{ option }"
                :options="flattenedFilteredOptions ?? []"
                v-bind="virtualizedBindings"
              >
                <slot
                  v-if="'optionItem' in slots"
                  name="optionItem"
                  :option="option as KaComboboxOptionProps"
                />
                <KaComboboxOption v-else v-bind="option as KaComboboxOptionProps" />
              </ComboboxVirtualizer>
            </template>

            <template v-for="option in filteredOptions" v-else :key="optionKey(option)">
              <KaComboboxGroup v-if="isGroup(option)" v-bind="option" />

              <template v-else>
                <slot v-if="'optionItem' in slots" name="optionItem" :option="option" />
                <KaComboboxOption v-else v-bind="option" />
              </template>
            </template>

            <KaComboboxCreateOption
              v-if="showCreateRow && createOptionConfig?.position === 'bottom'"
              :search-term="searchTerm"
              :create-label="props.createLabel"
              :create-label-empty="props.createLabelEmpty"
              @create="onCreateOption($event)"
            />
          </template>

          <KaInfiniteLoader
            v-if="props.paginated && !shouldShowEmptyState && !isFiltering"
            :data-testid="suffix('infinite-loader')"
            v-bind="props.paginated"
            hide-bottom-message
            :data="flattenedFilteredOptions"
            @load-more="props.paginated.onLoadMore"
          />
        </div>
      </ComboboxContent>
    </ComboboxPortal>

    <KaMessage
      v-if="!props.showErrorStylesOnly && hasError"
      :id="fallbackErrorMessageId"
      :data-testid="suffix('error-message')"
      :message="props.errorMessage"
      background="transparent"
      variant="error"
      :role="null"
      :class="['mt-2', { 'sr-only': props.hideErrorMessage }]"
    />

    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      class="sr-only"
      :data-testid="suffix('filter-count-announcement')"
    >
      {{ filterCountAnnouncement }}
    </div>
  </ComboboxRoot>
</template>
<style>
@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes scale-out {
  from {
    opacity: 1;
    filter: blur(0px);
    transform: scale(1);
  }
  to {
    opacity: 0;
    filter: blur(20px);
    transform: scale(0.95);
  }
}
</style>
